import type { AiManagedGroupDiagnostics, AiManagedGroupPolicy } from "@adpilot/shared";
import { IconShieldCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { ManagedGroupDiagnosticsPanel } from "@/components/managed-group/ManagedGroupDiagnosticsPanel";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const GOAL_ITEMS = [
  "improve_efficiency",
  "grow_sales",
  "explore_and_learn",
  "defend_branded_traffic",
  "launch_product",
  "clear_inventory",
] as const;
const RISK_ITEMS = ["conservative", "balanced", "aggressive"] as const;
const MODE_ITEMS = ["recommendation", "semi-auto", "full-auto"] as const;

interface PolicyStepProps {
  alreadyAiOnCount: number;
  diagnostics?: AiManagedGroupDiagnostics;
  diagnosticsError: boolean;
  diagnosticsLoading: boolean;
  policy: AiManagedGroupPolicy;
  selectedCount: number;
  onRetryDiagnostics: () => void;
  onChange: React.Dispatch<React.SetStateAction<AiManagedGroupPolicy>>;
}

export function PolicyStep({
  alreadyAiOnCount,
  diagnostics,
  diagnosticsError,
  diagnosticsLoading,
  policy,
  selectedCount,
  onRetryDiagnostics,
  onChange,
}: PolicyStepProps): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  return (
    <div className="flex h-full gap-4 px-[22px] py-[18px]">
      <div className="flex w-[560px] shrink-0 flex-col gap-[17px]">
        <div data-policy-field="target-acos" className="flex shrink-0 flex-col gap-1.5">
          <Label htmlFor="managed-group-target-acos" className="text-[11px] leading-[14px]">
            {t("policy.targetAcos")}
          </Label>
          <div className="relative">
            <Input
              id="managed-group-target-acos"
              type="number"
              min="1"
              max="200"
              step="1"
              className="hide-number-spin [&_[data-slot=input]]:pr-8"
              value={Math.round(policy.targetAcos * 100)}
              onChange={(event) =>
                onChange((current) => ({
                  ...current,
                  targetAcos: Number(event.target.value) / 100,
                }))
              }
            />
            <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-xs text-muted-foreground">
              %
            </span>
          </div>
        </div>

        <fieldset
          aria-label={t("policy.riskPreference")}
          data-slot="choice-card-group"
          className="flex min-w-0 flex-col gap-[7px]"
        >
          <legend className="text-[11px] leading-[14px] font-medium">
            {t("policy.riskPreference")}
          </legend>
          <div className="grid grid-cols-3 gap-2">
            {RISK_ITEMS.map((risk) => (
              <ChoiceCard
                key={risk}
                selected={policy.riskPreference === risk}
                title={t(`policy.risks.${risk}`)}
                description={t(`policy.riskDescriptions.${risk}`)}
                onClick={() => onChange((current) => ({ ...current, riskPreference: risk }))}
              />
            ))}
          </div>
        </fieldset>

        <div data-policy-field="business-goal" className="flex flex-col gap-[7px]">
          <Label className="text-[11px] leading-[14px]">{t("policy.businessGoal")}</Label>
          <div className="grid grid-cols-2 gap-2">
            {GOAL_ITEMS.map((goal) => (
              <ChoiceCard
                key={goal}
                selected={policy.businessGoal === goal}
                title={t(`policy.goals.${goal}`)}
                description={t(`policy.goalDescriptions.${goal}`)}
                showRadio
                onClick={() => onChange((current) => ({ ...current, businessGoal: goal }))}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-[7px]">
          <div className="flex justify-between">
            <Label className="text-[11px] leading-[14px]">{t("policy.executionMode")}</Label>
            <span className="text-[9px] leading-3 text-muted-foreground">
              {t("policy.executionModeHint")}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {MODE_ITEMS.map((mode) => (
              <ChoiceCard
                key={mode}
                selected={policy.executionMode === mode}
                title={t(`policy.modes.${mode}`)}
                description={t(`policy.modeDescriptions.${mode}`)}
                onClick={() => onChange((current) => ({ ...current, executionMode: mode }))}
              />
            ))}
          </div>
        </div>

        <div
          data-slot="workspace-guardrails"
          className="flex h-[58px] shrink-0 items-center gap-2.5 rounded-[8px] border border-badge-success-border bg-badge-success-background px-3"
        >
          <span
            data-slot="workspace-guardrails-icon"
            className="flex size-4 shrink-0 items-center justify-center"
          >
            <IconShieldCheck
              aria-hidden
              width={16}
              height={16}
              viewBox="0 0 24 24"
              strokeWidth={1.75}
              className="text-badge-success-foreground"
            />
          </span>
          <div className="flex flex-col gap-px">
            <p className="text-[10px] leading-3 font-semibold text-badge-success-foreground">
              {t("policy.workspaceGuardrails")}
            </p>
            <p className="text-[9px] leading-[14px] text-success-foreground">
              {t("policy.guardrails")}
            </p>
          </div>
        </div>
      </div>

      <aside className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto pt-[21px] paper-scrollbar">
        <ManagedGroupDiagnosticsPanel
          diagnostics={diagnostics}
          isLoading={diagnosticsLoading}
          isError={diagnosticsError}
          onApplyRecommendation={(targetAcos) =>
            onChange((current) => ({ ...current, targetAcos }))
          }
          onRetry={onRetryDiagnostics}
        />
        <p className="text-[9px] leading-[14px] text-muted-foreground">
          {t("policy.impactHint", {
            turnsOn: selectedCount - alreadyAiOnCount,
            alreadyOn: alreadyAiOnCount,
          })}
        </p>
        <InlineNotice>{t("policy.snapshotNotice")}</InlineNotice>
      </aside>
    </div>
  );
}

function ChoiceCard({
  description,
  onClick,
  selected,
  showRadio = false,
  title,
}: {
  description: string;
  onClick: () => void;
  selected: boolean;
  showRadio?: boolean;
  title: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      className={cn(
        "flex min-h-[58px] gap-2 rounded-[8px] border px-2.5 py-[9px] text-left",
        selected ? "border-foreground" : "border-line hover:bg-subtle/50",
      )}
      aria-pressed={selected}
      onClick={onClick}
    >
      {showRadio ? (
        <span
          className={cn(
            "mt-px flex size-4 shrink-0 items-center justify-center rounded-full border",
            selected ? "border-foreground" : "border-line",
          )}
        >
          {selected ? <span className="size-2 rounded-full bg-foreground" /> : null}
        </span>
      ) : null}
      <span className="flex min-w-0 flex-col gap-px">
        <span className="text-[11px] leading-[14px] font-semibold">{title}</span>
        <span className="text-[9px] leading-3 text-muted-foreground">{description}</span>
      </span>
    </button>
  );
}

function InlineNotice({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <Alert
      variant="warning"
      className="grid-cols-[6px_1fr] gap-x-2 rounded-[10px] p-3 text-warning-foreground"
    >
      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-warning" />
      <AlertDescription className="col-start-2 text-[9px] leading-[14px] text-warning-foreground">
        {children}
      </AlertDescription>
    </Alert>
  );
}
